// ════════════════════════════════════════════════════════════════════════════
//  Importação das NF-e emitidas em OUTRO sistema, pelos XML autorizados (histórico
//  fiscal). O usuário escolhe os zips (um por mês) ou XML soltos; primeiro CONFERE
//  (nada é gravado) e depois IMPORTA. Os arquivos vão um por vez, para cada envio
//  caber no tempo de uma requisição. Reenviar o mesmo arquivo não duplica nada.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileArchive, RefreshCw, Upload, XCircle } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiClient';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import type { NfeImportacaoResultado } from '@/features/fiscal/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Props {
  open: boolean;
  onFechar: () => void;
  /** Chamado depois de uma importação de verdade (a lista de notas ganha as linhas novas). */
  onImportado: () => void;
}

type Situacao = 'aguardando' | 'enviando' | 'ok' | 'erro';
interface Envio { arquivo: File; situacao: Situacao; resultado?: NfeImportacaoResultado; erro?: string }

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const mesExtenso = (mes: string) => {
  const [a, m] = mes.split('-');
  return `${m}/${a}`;
};
const MAX_OCORRENCIAS = 300;

export function ImportarNotasEmitidasDialog({ open, onFechar, onImportado }: Props) {
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [modo, setModo] = useState<'conferencia' | 'importacao' | null>(null);
  const [rodando, setRodando] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setEnvios([]); setModo(null);
  }, [open]);

  const escolher = (arquivos: FileList | null) => {
    if (!arquivos) return;
    setEnvios([...arquivos].sort((a, b) => a.name.localeCompare(b.name)).map((arquivo) => ({ arquivo, situacao: 'aguardando' })));
    setModo(null);
  };

  const executar = async (simular: boolean) => {
    setRodando(true);
    setModo(simular ? 'conferencia' : 'importacao');
    setEnvios((e) => e.map((x) => ({ arquivo: x.arquivo, situacao: 'aguardando' })));
    let importouAlgo = false;
    for (let i = 0; i < envios.length; i++) {
      setEnvios((e) => e.map((x, j) => (j === i ? { ...x, situacao: 'enviando' } : x)));
      try {
        const resultado = await notaFiscalService.importarEmitidas(envios[i].arquivo, simular);
        importouAlgo ||= !simular && resultado.importadas + resultado.canceladas > 0;
        setEnvios((e) => e.map((x, j) => (j === i ? { ...x, situacao: 'ok', resultado } : x)));
      } catch (err) {
        setEnvios((e) => e.map((x, j) => (j === i ? { ...x, situacao: 'erro', erro: getApiErrorMessage(err) } : x)));
      }
    }
    setRodando(false);
    if (importouAlgo) onImportado();
  };

  // Soma dos envios — o relatório da conferência ou da importação, como um todo.
  const total = useMemo(() => {
    const r = envios.map((e) => e.resultado).filter((x): x is NfeImportacaoResultado => !!x);
    const soma = (f: (x: NfeImportacaoResultado) => number) => r.reduce((s, x) => s + f(x), 0);
    const meses = new Map<string, { notas: number; canceladas: number; total: number; totalCanceladas: number }>();
    r.flatMap((x) => x.porMes).forEach((m) => {
      const a = meses.get(m.mes) ?? { notas: 0, canceladas: 0, total: 0, totalCanceladas: 0 };
      meses.set(m.mes, {
        notas: a.notas + m.notas, canceladas: a.canceladas + m.canceladas,
        total: a.total + m.total, totalCanceladas: a.totalCanceladas + m.totalCanceladas,
      });
    });
    return {
      tem: r.length > 0,
      notasLidas: soma((x) => x.notasLidas), importadas: soma((x) => x.importadas),
      jaExistentes: soma((x) => x.jaExistentes), canceladas: soma((x) => x.canceladas),
      recusadas: soma((x) => x.recusadas), titulos: soma((x) => x.titulosGerados),
      totalTitulos: soma((x) => x.totalTitulos), totalImportado: soma((x) => x.totalImportado),
      eventos: soma((x) => x.eventosRegistrados),
      meses: [...meses.entries()].sort(([a], [b]) => a.localeCompare(b)),
      ocorrencias: r.flatMap((x) => x.ocorrencias),
      naturezasNovas: [...new Set(r.flatMap((x) => x.naturezasNovas ?? []))],
      clientesNovos: [...new Set(r.flatMap((x) => x.clientesNovos))],
      produtosNovos: [...new Set(r.flatMap((x) => x.produtosNovos))],
    };
  }, [envios]);

  const conferindo = modo === 'conferencia';
  const podeImportar = envios.length > 0 && !rodando && modo === 'conferencia'
    && envios.every((e) => e.situacao === 'ok');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !rodando) onFechar(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar notas emitidas em outro sistema</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Envie os XML das NF-e que a empresa já emitiu (um <strong>.zip por mês</strong>, ou arquivos .xml), junto com os
            XML de cancelamento. As notas entram como foram autorizadas, para o histórico fiscal: <strong>nada é enviado à
            SEFAZ</strong> e <strong>o estoque não é movimentado</strong>. Cada duplicata vira um título a receber
            <strong> em aberto</strong>, e cliente ou produto que não estiver cadastrado é criado.
            Primeiro confira — nada é gravado — e depois importe. Reenviar o mesmo arquivo não duplica nada.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <input ref={input} type="file" multiple accept=".zip,.xml" className="hidden"
              onChange={(e) => { escolher(e.target.files); e.target.value = ''; }} />
            <Button variant="outline" className="gap-2" onClick={() => input.current?.click()} disabled={rodando}>
              <FileArchive className="h-4 w-4" /> Escolher arquivos
            </Button>
            {envios.length > 0 && (
              <span className="text-muted-foreground">{envios.length} arquivo{envios.length > 1 ? 's' : ''} escolhido{envios.length > 1 ? 's' : ''}</span>
            )}
          </div>

          {envios.length > 0 && (
            <ul className="space-y-1">
              {envios.map((e) => (
                <li key={e.arquivo.name} className="flex items-center gap-2">
                  {e.situacao === 'enviando' ? <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    : e.situacao === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    : e.situacao === 'erro' ? <XCircle className="h-3.5 w-3.5 text-destructive" />
                    : <span className="inline-block h-3.5 w-3.5" />}
                  <span className="font-mono text-xs">{e.arquivo.name}</span>
                  {e.resultado && (
                    <span className="text-xs text-muted-foreground">
                      {e.resultado.notasLidas} notas · {e.resultado.importadas} {e.resultado.simulacao ? 'a importar' : 'importadas'}
                      {e.resultado.jaExistentes > 0 && ` · ${e.resultado.jaExistentes} já existentes`}
                      {e.resultado.recusadas > 0 && ` · ${e.resultado.recusadas} recusadas`}
                    </span>
                  )}
                  {e.erro && <span className="text-xs text-destructive">{e.erro}</span>}
                </li>
              ))}
            </ul>
          )}

          {total.tem && (
            <>
              <div className="rounded-md border p-3 space-y-1">
                <p className="font-medium">
                  {conferindo ? 'Conferência — nada foi gravado' : 'Importação concluída'}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                  <span>Notas lidas: <strong>{total.notasLidas}</strong></span>
                  <span>{conferindo ? 'A importar' : 'Importadas'}: <strong>{total.importadas}</strong></span>
                  <span>Já existentes: <strong>{total.jaExistentes}</strong></span>
                  <span>Canceladas: <strong>{total.canceladas}</strong></span>
                  <span className={total.recusadas > 0 ? 'text-destructive' : ''}>Recusadas: <strong>{total.recusadas}</strong></span>
                  <span>Valor {conferindo ? 'a importar' : 'importado'}: <strong>{moeda(total.totalImportado)}</strong></span>
                  <span>Títulos a receber: <strong>{total.titulos}</strong> ({moeda(total.totalTitulos)})</span>
                  {total.eventos > 0 && <span>Cartas de correção: <strong>{total.eventos}</strong></span>}
                </div>
              </div>

              {total.meses.length > 0 && (
                <div>
                  <p className="font-medium mb-1">Conferência por mês de emissão (todas as notas dos arquivos)</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mês</TableHead>
                        <TableHead className="text-right">Notas</TableHead>
                        <TableHead className="text-right">Canceladas</TableHead>
                        <TableHead className="text-right">Total das notas</TableHead>
                        <TableHead className="text-right">Sem as canceladas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {total.meses.map(([mes, m]) => (
                        <TableRow key={mes}>
                          <TableCell>{mesExtenso(mes)}</TableCell>
                          <TableCell className="text-right tabular-nums">{m.notas}</TableCell>
                          <TableCell className="text-right tabular-nums">{m.canceladas}</TableCell>
                          <TableCell className="text-right tabular-nums">{moeda(m.total)}</TableCell>
                          <TableCell className="text-right tabular-nums">{moeda(m.total - m.totalCanceladas)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {total.naturezasNovas.length > 0 && (
                <div className="rounded-md border p-3 space-y-1">
                  <p className="font-medium">
                    Naturezas da operação que não estão no cadastro{conferindo ? ' — serão cadastradas (inativas)' : ' — cadastradas (inativas)'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    É a natureza que decide se a nota entra nos relatórios de vendas. Ajuste em Cadastros → Fiscal →
                    Natureza de Operação ("Conta como venda"). Nos relatórios fiscais a nota entra sempre.
                  </p>
                  <ul className="ml-4 list-disc text-xs">
                    {total.naturezasNovas.map((n) => <li key={n}>{n}</li>)}
                  </ul>
                </div>
              )}

              {(total.clientesNovos.length > 0 || total.produtosNovos.length > 0) && (
                <div className="space-y-1">
                  {total.clientesNovos.length > 0 && (
                    <details>
                      <summary className="cursor-pointer">
                        {total.clientesNovos.length} cliente{total.clientesNovos.length > 1 ? 's' : ''} sem cadastro
                        {conferindo ? ' — serão cadastrados' : ' — cadastrados'}
                      </summary>
                      <ul className="mt-1 ml-4 list-disc text-xs text-muted-foreground">
                        {total.clientesNovos.map((c) => <li key={c}>{c}</li>)}
                      </ul>
                    </details>
                  )}
                  {total.produtosNovos.length > 0 && (
                    <details>
                      <summary className="cursor-pointer">
                        {total.produtosNovos.length} produto{total.produtosNovos.length > 1 ? 's' : ''} sem cadastro
                        {conferindo ? ' — serão cadastrados' : ' — cadastrados'} (código do produto do XML)
                      </summary>
                      <ul className="mt-1 ml-4 list-disc text-xs text-muted-foreground">
                        {total.produtosNovos.map((p) => <li key={p}>{p}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {total.ocorrencias.length > 0 && (
                <div>
                  <p className="font-medium mb-1">
                    Recusas e avisos ({total.ocorrencias.length})
                    {total.ocorrencias.length > MAX_OCORRENCIAS && ` — mostrando os ${MAX_OCORRENCIAS} primeiros`}
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Situação</TableHead>
                        <TableHead>Nota</TableHead>
                        <TableHead>Arquivo</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {total.ocorrencias.slice(0, MAX_OCORRENCIAS).map((o, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Badge variant="outline" className={o.situacao === 'recusada' ? 'text-destructive border-destructive' : ''}>
                              {o.situacao === 'recusada' ? 'Recusada' : o.situacao === 'ignorada' ? 'Ignorada' : 'Aviso'}
                            </Badge>
                          </TableCell>
                          <TableCell className="tabular-nums">{o.numero ?? '—'}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[180px] truncate" title={o.arquivo}>{o.arquivo || '—'}</TableCell>
                          <TableCell className="text-xs">{o.mensagem}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={rodando}>Fechar</Button>
          <Button variant="outline" className="gap-2" onClick={() => executar(true)} disabled={envios.length === 0 || rodando}>
            {rodando && conferindo ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Conferir
          </Button>
          <Button className="gap-2" onClick={() => executar(false)} disabled={!podeImportar}
            title={podeImportar ? undefined : 'Confira os arquivos antes de importar'}>
            {rodando && !conferindo ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
